import { useCallback, memo } from 'react';
import { ChevronDown } from 'lucide-react';

import { useToggle } from '@/utils/hooks';

import { ParticipantCard } from './participantCard/participantCard';
import { TestId } from '@/types/testId';
import { cn } from '@/shadcn/lib/utils';
import { Badge } from '@/shadcn/badge';
import { pluralize } from '@/utils';
import { Button } from '@/shadcn/button';

type Props = {
  title?: string;
  participants: any[];
  teamAccountId?: string | null;
  removeGroupMembers: (accountIds: string[], title: string) => void;
};

export const ParticipantsList = memo(
  ({
    title = '',
    participants,
    teamAccountId = null,
    removeGroupMembers
  }: Props) => {
    const [displayParticipants, toggleDisplayParticipants] = useToggle(true);
    const hasParticipants = participants.length > 0;

    const removeAll = useCallback(() => {
      removeGroupMembers(
        teamAccountId
          ? [teamAccountId]
          : participants.map(participant => participant.accountId),
        title
      );
    }, [participants, teamAccountId, removeGroupMembers]);

    return (
      <div className="mt-2.5 flex flex-col flex-nowrap items-stretch">
        <div className="flex flex-row flex-nowrap items-center justify-between">
          <div
            className={cn(
              'text-foreground my-2 flex flex-row flex-nowrap items-center gap-3 text-base font-semibold capitalize',
              hasParticipants && 'cursor-pointer'
            )}
            onClick={hasParticipants ? toggleDisplayParticipants : undefined}
            data-cy={TestId.ParticipantsList.ListToggle}
          >
            <p>{title ?? 'Undefined team name'}</p>

            {hasParticipants && (
              <ChevronDown
                size={18}
                className={cn(
                  'shrink-0 transition-transform ease-out',
                  !displayParticipants && 'rotate-180'
                )}
              />
            )}
            <Badge
              variant="card"
              className="pointer-events-none cursor-default text-xs normal-case select-none"
              tabIndex={-1}
            >
              {pluralize(participants.length, 'member')}
            </Badge>
          </div>
          <Button
            onClick={removeAll}
            variant="ghostDestructiveSubtle"
            className="normal-case"
            size="sm"
            data-cy={TestId.ParticipantsList.RemoveAllButton}
          >
            {`Remove ${teamAccountId ? 'team' : 'all'}`}
          </Button>
        </div>
        {hasParticipants && displayParticipants && (
          <div className="flex flex-col gap-2">
            {participants.map(({ accountId, entity: participant }) => (
              <div key={participant.rowId}>
                <ParticipantCard
                  participant={participant}
                  participantAccountId={accountId} // note: if within a team, the participant has an account id of null and hence will not be removable
                  removeGroupMembers={removeGroupMembers}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
);
